/**
 * The dashboard's **Ledger** tab (spec 2026-10-09 §2.2 tab 7, W4 card C4): registers its four sections (Finances,
 * Standing, Law, Corporation; `registerDashSection`). Read-only over the Career (`m2` service), the strategic client
 * (relations, the system view's claim, `captainView`, `corpView()`) and the Reach (known stations for the docked-only
 * actions' Set course). No save field, no per-frame work: the bodies poll only while the dashboard is open.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { FACTION_OUTLAW } from '../../charter/hostilityWriter'
import { strategicFactionOf } from '../../charter/careerBridge'
import lawJson from '../../data/strategic/law.json'
import { formatDistance } from '../../hud/reach/format'
import { loadDashboardConfig } from '../../ui/dashboard/config'
import { createReachStationDirectory, setCourseTo } from '../../ui/dashboard/data'
import type { StationDirectory } from '../../ui/dashboard/data/types'
import { dashboardOpen, registerDashSection } from '../../ui/dashboard/registry'
import { createLedgerSections } from '../../ui/dashboard/tabs/ledger/sections'
import { factionName } from '../../ui/strategic/names'
import { Pos64 } from '../../world/components'
import { FACTION, type FactionId } from '../../strategic/types'

/** After the strategic client and the corporation panels (strategic + 4). */
const ORDER = FEATURE_ORDER.strategic + 6

export function createLedgerFeature(): GameFeature & { dispose(): void } {
  const offs: (() => void)[] = []
  return {
    name: 'ledger',
    order: ORDER,
    init(ctx: GameContext): void {
      const reach = ctx.reach
      const strategic = (): ReturnType<typeof ctx.services.maybe<'strategic'>> => ctx.services.maybe('strategic')
      let dir: StationDirectory | null = null
      const directory = (): StationDirectory | null => {
        if (reach === null) return null
        dir ??= createReachStationDirectory({
          galaxy: reach.galaxy,
          state: reach.state,
          layoutOf: (id) => reach.layoutOf(id),
          hereSystemId: () => reach.session.layout?.systemId ?? null,
          herePosition: () => {
            const p = ctx.sim.playerEid
            return p < 0 ? null : [Pos64.x[p], Pos64.y[p], Pos64.z[p]]
          },
        })
        return dir
      }
      const sections = createLedgerSections({
        dash: loadDashboardConfig(),
        career: () => ctx.services.maybe('m2')?.career() ?? null,
        relations: () => strategic()?.relations ?? null,
        claim: () => {
          if (reach?.session.layout == null) return undefined
          const v = strategic()?.systemView ?? null
          if (v === null) return null
          return { holder: v.claim.holder, corpName: v.claim.corp?.name }
        },
        captains: () => strategic()?.captainView(null) ?? null,
        corp: () => strategic()?.corpView() ?? null,
        directory,
        setCourse: (d, stationId) => {
          if (reach !== null) setCourseTo(reach, d, stationId)
        },
        systemName: (id) => reach?.galaxy.systems.find((s) => s.id === id)?.name ?? id,
        factionName: (f: FactionId) => factionName(f),
        simFactionName: (f) => factionName(strategicFactionOf(f)),
        isOutlaw: (f: FactionId) => FACTION_OUTLAW[FACTION[f]] === 1,
        finesMult: lawJson.fines.mult,
        finesRange: formatDistance(lawJson.fines.rangeM),
        isOpen: dashboardOpen,
      })
      for (const s of sections) offs.push(registerDashSection(s))
    },
    dispose(): void {
      for (const off of offs.splice(0)) off()
    },
  }
}

const feature = createLedgerFeature()

export default feature
